<script setup lang="ts">
import { watchEffect, ref, defineProps } from 'vue'
import { useRoute } from 'vue-router'
import { getUser, http } from '../composables/http'
import ContactCheckbox from './ContactCheckbox.vue'
import ContactForm from './ContactForm.vue'
import ContactCard from './ContactCard.vue'

const { phone, email } = defineProps(['phone', 'email'])
// `ref([])` e `ref({})` sao inferidos como `never[]` e `{}`, e ai TODO acesso a uma propriedade do
// contato ou do cliente no template vira erro de tipo — eram 31 dos 58 do projeto. O dado vem de
// JSON sem contrato, entao `any` e' o que ele e' de verdade; mentir um tipo aqui seria pior.
const contacts = ref<any[]>([])
const customer = ref<any>({})
const message = ref('')
const user = ref(getUser())

const loadContacts = async () => {
  const resp: any = await http(`/contacts/${phone}/analyze`)
  if (!resp?.error && resp?.data && resp?.data.contacts && resp?.data.contacts[0]) {
    contacts.value = resp?.data.contacts.sort((a: any, b: any) => a.id - b.id)
  }
  customer.value = ((contacts?.value || [])[0] || {})['customer']
}

// A acao que veio no link do Google Chat (`?contact=<id>&mark=unknown|verified`), mandada pelo
// ContactUnknownAnalysisJob quando ele revisa um numero marcado como de outra pessoa.
//
// Nao executa direto: pede o MOTIVO primeiro. E' ele que vai para o ContactTransition, e sem ele o
// registro guarda quem e quando mas nao o unico dado que alguem vai querer depois — por que. A API
// tambem o exige quando o valor vem explicito, entao pedir aqui nao e' gentileza de tela.
//
// Nao passa pelo `user.admin` das caixinhas de proposito: quem autoriza aqui e' o ContactPolicy, no
// servidor, pelo acesso do usuario a unidade daquele paciente.
// Toda marcacao — a caixinha da tela e o link que a Bete manda no Google Chat — passa por aqui e
// pede o MOTIVO antes de executar. E' ele que vai para o ContactTransition, e a API o exige: e' a
// unica informacao que o registro guarda e que o codigo nao tem como inventar. O estado esta na
// coluna, o autor vem do token, a data no relogio; o porque so' quem marcou sabe.
const route = useRoute()

// O que cada acao afirma, para a frase da confirmacao e a do aviso. A chave e' a rota, que em duas
// delas nao usa o nome da coluna: `fixed` e' `fix` e `blocked` e' `block`.
const ACTIONS: Record<string, { says: string, column: string }> = {
  unknown: { says: 'não é o número do paciente', column: 'unknown' },
  verified: { says: 'é o número do paciente', column: 'verified' },
  fix: { says: 'é o número que sempre será usado', column: 'fixed' },
  block: { says: 'nunca deve ser usado', column: 'blocked' },
  message: { says: 'serve só para recado', column: 'message' }
}
const pendingMark = ref<{ id: string, action: string, value: boolean | null } | null>(null)
const reason = ref('')
const saving = ref(false)
let markAsked = false

// A escolha que vem do link do Chat. `value: true` explicito, e nao alternar: entre o aviso e o
// clique alguem pode mexer no contato, e o link tem de fazer o que promete.
const askMark = () => {
  const action = String(route.query?.mark || '')
  const id = route.query?.contact
  if (markAsked || !id || !ACTIONS[action]) return
  markAsked = true
  pendingMark.value = { id: String(id), action, value: true }
}

// A caixinha da tela: alterna, entao vai sem valor. Mas pede o motivo do mesmo jeito.
const askToggle = (contact: any, action: string) => {
  if (!ACTIONS[action]) return
  reason.value = ''
  pendingMark.value = { id: String(contact.id), action, value: null }
}

const cancelMark = () => {
  pendingMark.value = null
  reason.value = ''
}

const confirmMark = async () => {
  if (!pendingMark.value || !reason.value.trim() || saving.value) return
  saving.value = true
  const { id, action, value } = pendingMark.value
  const body: Record<string, any> = { reason: reason.value.trim() }
  if (value !== null) body.value = value
  const resp: any = await http(`/contacts/${id}/${action}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  })
  saving.value = false
  if (resp?.error) {
    message.value = `Não consegui marcar o contato: ${resp.error}`
    return
  }
  cancelMark()
  message.value = 'Pronto, registrei a marcação com o motivo.'
  return loadContacts()
}

// O motivo atual de cada marca, para o title da caixinha — vem do `reasons` do /analyze, que traz a
// ultima transicao de cada uma.
const reasonFor = (contact: any, column: string) => {
  const t = contact?.reasons?.[column]
  if (!t) return ''
  const quando = t.at ? new Date(t.at).toLocaleString('pt-BR') : ''
  return `${t.value ? 'Marcado' : 'Desmarcado'} por ${t.author} em ${quando}: ${t.reason || 'sem motivo'}`
}

watchEffect(async () => {
  await loadContacts()
  return askMark()
})

// Nao manda mais direto: abre o campo de motivo, e quem executa e' o confirmMark.
const updateContact = async (contact: any, type: string) => {
  if (!user?.value?.admin) {
    return
  }
  askToggle(contact, type)
}
const onSaveSuccess = async () => {
  customer.value = {}
  return loadContacts()
}
const onSaveFail = async (text: string) => {
  return message.value = text
}
const setCustomer = async (contact: any) => {
  customer.value = contact?.customer
}

const phoneFormat = (contact: { value: string }) => {
  return `${contact.value} ${phone == contact.value || (email && contact.value && email.toLowerCase().trim() == contact.value.toLowerCase().trim()) ? ' *' : ''}`
}
</script>

<style lang="css">
.vertical-text {
  writing-mode: vertical-rl !important;
  text-orientation: sideways !important; /* or  */
}
</style>

<template>
  <div v-if="message" class="flex items-center justify-between mt-4">
    {{ message }}
  </div>

  <!-- A escolha que vem do link do Google Chat. O motivo e' obrigatorio: e' o que o ContactTransition
       guarda para o proximo atendente entender por que o numero esta como esta. -->
  <div v-if="pendingMark" class="p-4 mt-4 bg-white rounded-md shadow-md">
    <p class="text-sm text-gray-700">
      Confirmar que <strong>{{ ACTIONS[pendingMark.action].says }}</strong>. Diga o motivo:
    </p>
    <textarea
      v-model="reason"
      rows="2"
      placeholder="Ex.: falei com o paciente e ele confirmou o número"
      class="block w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
    />
    <div class="flex items-center mt-2">
      <button
        type="button"
        :disabled="!reason.trim() || saving"
        class="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md disabled:bg-gray-400"
        @click="confirmMark"
      >
        Confirmar
      </button>
      <button type="button" class="px-4 py-2 ml-2 text-sm text-gray-700" @click="cancelMark">
        Cancelar
      </button>
    </div>
  </div>

  <ContactForm
      :customer="customer"
      v-if="user?.admin"
      @on-save-success="onSaveSuccess"
      @on-save-fail="onSaveFail" />
    
  <table  class="w-full text-center table-auto min-w-max" v-if="contacts.length > 0">
    <thead>
      <tr>
        <th>Contato</th>
        <th>Cliente</th>
        <th>Unidade</th>
        <th class="vertical-text" title="S - Sisodonto, B - Bigdatacorp, A - Assertiva">Origem</th>
        <th class="vertical-text" title="Já recebeu mensagem">Validado</th>
        <th class="vertical-text" title="Já agendou tratamento ou confirmou que é ele">Verificado</th>
        <th class="vertical-text" title="Tem Whatsapp">Whatsapp</th>
        <th class="vertical-text" title="Ultimo telefone fornecido na presença das consultas ou pagamento como titular">Último</th>
        <th class="vertical-text" title="Sempre será usado">Fixado</th>
        <th class="vertical-text" title="Nunca será usado">Bloqueado</th>
        <th class="vertical-text" title="Desconhecido">Desconhecido</th>
        <th class="vertical-text" title="A operadora informou que o número não existe">Inexistente</th>
        <th class="vertical-text" title="Contato para recado">Recado</th>
        <th class="vertical-text" title="Telefone de quem indicou o paciente">Indicador</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="contact in contacts" :key="contact.id" @click="setCustomer(contact)">
        <td>
          <a v-if="contact.link" :href="contact.link" target="_blank">{{ phoneFormat(contact) }}</a>
          <span v-else>{{ phoneFormat(contact) }}</span>
        </td>
        <td>{{ contact.customer.name }}{{ contact.description ? `(${contact.description})` : '' }}</td>
        <td>{{ contact.customer.company.name }}</td>
        <td>{{ (contact.origin || '')[0].toUpperCase() }}</td>
        <td><ContactCheckbox :contact-id="contact.id" :checked="contact.validated" disabled="true" property="validated"/></td>
        <td><ContactCheckbox :contact-id="contact.id" :checked="contact.verified" disabled="true" :title="reasonFor(contact, 'verified')" property="verified"/></td>
        <td><ContactCheckbox :contact-id="contact.id" :checked="contact.whatsapp" disabled="true" property="whatsapp"/></td>
        <td><ContactCheckbox :contact-id="contact.id" :checked="contact.last" disabled="true" property="last"/></td>
        <td><ContactCheckbox :contact-id="contact.id" @updateContact="updateContact(contact, 'fix')" :disabled="!user?.admin || contact.blocked || contact.message || contact.unknown" :checked="contact.fixed" :title="reasonFor(contact, 'fixed')" property="fixed"/></td>
        <td><ContactCheckbox :contact-id="contact.id" @updateContact="updateContact(contact, 'block')" :disabled="!user?.admin || contact.fixed || contact.message || contact.unknown" :checked="contact.blocked" :title="reasonFor(contact, 'blocked')" property="blocked"/></td>
        <td><ContactCheckbox :contact-id="contact.id" @updateContact="updateContact(contact, 'unknown')" :disabled="!user?.admin || contact.blocked || contact.fixed || contact.message" :checked="contact.unknown" :title="reasonFor(contact, 'unknown')" property="unknown"/></td>
        <td><ContactCheckbox :contact-id="contact.id" :checked="contact.not_found" disabled="true" :title="reasonFor(contact, 'not_found')" property="not_found"/></td>
        <td><ContactCheckbox :contact-id="contact.id" @updateContact="updateContact(contact, 'message')" :disabled="!user?.admin || contact.blocked || contact.fixed || contact.unknown" :checked="contact.message" :title="reasonFor(contact, 'message')" property="message"/></td>
        <td><ContactCheckbox :contact-id="contact.id" :checked="contact.indicator" disabled="true" :title="reasonFor(contact, 'indicator')" property="indicator"/></td>
      </tr>
    </tbody>
  </table>
  <span v-else>
    Oi, aqui é a Bete, não consegui qual o paciente desse contato!
  </span>

  <div class="flex items-center justify-between mt-4"/>

  <ContactCard
      :customer="customer"/>
</template>
